import { c as _c } from "react/compiler-runtime";
import { useRef, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Archive, ArchiveRestore, ArrowDown, ArrowUp, ChevronDown, ChevronRight, ImageOff, ListChecks, Pencil, Plus, Upload, X } from "lucide-react";
import { SIZE_GRIDS, SIZE_GRID_IDS } from "@osmix/catalog-core/size-grids";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { allowedParents, buildCategoryTree, type CategoryTreeRow, subtreeIds } from "@/lib/category-tree";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { useAuth } from "@/store";
import { api, type Category } from "@/api";
import { categoryKeys, useCategories } from "@/api/hooks/use-categories";
import { CategorySchemaEditor } from "@/components/category-schema-editor";
import { ApplyRecommendedButton, ComplianceCell, type ComplianceDraft, ComplianceEditor, complianceDraftOf, complianceInputOf, complianceMatrixKey, resolveCompliance, useComplianceMatrix } from "@/components/category-compliance";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Select } from "@/components/ui/select";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
const SECTIONS = ["clothing", "shoes", "accessories", "beauty", "home", "kids", "other"] as const;
const GENDERS = ["female", "male", "unisex", "kids"] as const;
type Section = (typeof SECTIONS)[number];
type Gender = (typeof GENDERS)[number];
type Draft = {
  name: string;
  parentId: string;
  section: string;
  gender: string;
  sizeGrid: string;
  imageUrl: string;
};
const blankDraft: Draft = {
  name: "",
  parentId: "",
  section: "",
  gender: "",
  sizeGrid: "",
  imageUrl: ""
};
const COLS = 6;

/** Превью картинки категории; битая или пустая ссылка — заглушка, а не дыра. */
function CategoryImage(t0) {
  const $ = _c(8);
  const {
    url,
    label
  } = t0;
  const [broken, setBroken] = useState(null);
  if (!url || broken === url) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <ImageOff className="size-4" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== label) {
      t2 = <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-md border bg-muted text-muted-foreground" title={label} data-testid="category-image-fallback">{t1}</span>;
      $[1] = label;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t1;
  if ($[3] !== url) {
    t1 = () => setBroken(url);
    $[3] = url;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1 || $[6] !== url) {
    t2 = <img src={url} alt="" className="size-9 shrink-0 rounded-md border object-cover" onError={t1} data-testid="category-image" />;
    $[5] = t1;
    $[6] = url;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

/** Картинка категории для приложения и витрины: ссылка или загрузка файла. */
function ImageField({
  value,
  onChange
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const {
    t
  } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const upload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      onChange(await api.feed.uploadMedia(file));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploading(false);
    }
  };
  return <div className="flex flex-col gap-1">
			<div className="flex items-center gap-2">
				<CategoryImage url={value.trim() || null} label={t("categories.tree.imageNone")} />
				<Input value={value} placeholder={t("categories.tree.imagePlaceholder")} onChange={e_0 => onChange(e_0.target.value)} className="w-[240px]" data-testid="category-image-input" />
				<input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e_1 => {
        const file_0 = e_1.target.files?.[0];
        if (file_0) void upload(file_0);
        e_1.target.value = "";
      }} />
				<Button type="button" size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} title={t("categories.tree.imageUpload")} aria-label={t("categories.tree.imageUpload")}>
					<Upload className="size-4" />
				</Button>
				{value && <Button type="button" size="sm" variant="ghost" onClick={() => onChange("")} aria-label={t("categories.tree.imageClear")}>
						<X className="size-4" />
					</Button>}
			</div>
			{uploading && <span className="text-xs text-muted-foreground">{t("categories.tree.imageUploading")}</span>}
			{error && <span className="text-xs text-destructive">{error}</span>}
		</div>;
}

/**
 * Дерево категорий площадки — одно для витрины, приложения и кабинета
 * продавца (дерево v2, миграция 134): до трёх уровней, аудитория → тип → вид.
 *
 * - счётчик товаров — по всей ветке (узел и его потомки), ссылка открывает
 *   «Товары» с фильтром по узлу, который тоже включает ветку;
 * - порядок — стрелками среди соседей, перенос под другого родителя
 *   предлагает только узлы, где ветка не станет глубже трёх уровней (то же
 *   проверяет шлюз);
 * - узел не удаляется, а уходит в архив: скрыт у покупателя и продавца,
 *   новых товаров не принимает, ставки и история сохраняются; из архива его
 *   можно вернуть;
 * - раздел/пол, размерная сетка и характеристики задаются на узле и
 *   наследуются вниз по ветке, пока потомок не задал своё.
 */
export function CategoriesPage() {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuth(s => s.user);
  const canEdit = hasAdminPermission(user, "action.categories.manage");
  const {
    data,
    error: loadError
  } = useCategories();
  const items = data ?? null;
  const sectionCounts = useQuery({
    queryKey: [...categoryKeys.all, "section-counts"],
    queryFn: () => api.categories.sectionCounts()
  });
  const [actionError, setActionError] = useState<string | null>(null);
  const [creating, setCreating] = useState<Draft | null>(null);
  const [editing, setEditing] = useState<{
    id: string;
    draft: Draft;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [schemaOpen, setSchemaOpen] = useState<string | null>(null);
  const [archiving, setArchiving] = useState<CategoryTreeRow | null>(null);
  // Документ о соответствии (17.10.2026): правка узла и итог «Включить рекомендованные».
  const [complianceDraft, setComplianceDraft] = useState<ComplianceDraft | null>(null);
  const [complianceNotice, setComplianceNotice] = useState<string | null>(null);
  const error = actionError ?? (loadError ? loadError.message : null);
  const refresh = () => queryClient.invalidateQueries({
    queryKey: categoryKeys.all
  });
  const all = items ?? [];
  const tree = buildCategoryTree(all);
  const byId = new Map(all.map(c => [c.id, c]));
  const compliance = resolveCompliance(all);
  // Матрица «категория → документ» (31.10.2026): свои значения узла и рекомендация.
  const complianceMatrix = useComplianceMatrix();
  const matrixRowOf = (id: string) => complianceMatrix.data?.find(row => row.categoryId === id);
  const activeSiblings = (parentId: string | null) => tree.active.filter(r => r.category.parentId === parentId).map(r_0 => r_0.category);

  // Свёрнутые узлы. По умолчанию видны аудитории и типы (два уровня), виды
  // раскрываются по клику — так дерево из 59 узлов читается с одного экрана.
  const [collapsedState, setCollapsed] = useState<Set<string> | null>(null);
  const collapsed = collapsedState ?? new Set(tree.active.filter(r_1 => r_1.depth === 2 && r_1.hasChildren).map(r_2 => r_2.category.id));
  const isHidden = (row_0: CategoryTreeRow) => {
    for (let p = row_0.category.parentId; p; p = byId.get(p)?.parentId ?? null) if (collapsed.has(p)) return true;
    return false;
  };
  const toggle = (id_0: string) => {
    const next = new Set(collapsed);
    if (next.has(id_0)) next.delete(id_0);else next.add(id_0);
    setCollapsed(next);
  };
  const facetsPayload = (d: Draft) => ({
    section: (d.section || null) as Section | null,
    gender: (d.gender || null) as Gender | null
  });
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      await refresh();
      return true;
    } catch (e) {
      setActionError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const submitCreate = async () => {
    if (!creating || !creating.name.trim()) return;
    const draft = creating;
    const ok = await run(async () => {
      const created = await api.categories.create({
        name: draft.name.trim(),
        parentId: draft.parentId || null,
        sizeGrid: draft.sizeGrid || null,
        imageUrl: draft.imageUrl.trim() || null
      });
      if (draft.section || draft.gender) await api.categories.setFacets(created.id, facetsPayload(draft));
    });
    if (ok) setCreating(null);
  };
  const submitEdit = async () => {
    if (!editing || !editing.draft.name.trim()) return;
    const {
      id: id_1,
      draft: draft_0
    } = editing;
    const prev = byId.get(id_1);
    const ok_0 = await run(async () => {
      await api.categories.update(id_1, {
        name: draft_0.name.trim(),
        ...((prev?.parentId ?? "") !== draft_0.parentId ? {
          parentId: draft_0.parentId || null
        } : {}),
        sizeGrid: draft_0.sizeGrid || null,
        imageUrl: draft_0.imageUrl.trim() || null
      });
      if ((prev?.section ?? "") !== draft_0.section || (prev?.gender ?? "") !== draft_0.gender) await api.categories.setFacets(id_1, facetsPayload(draft_0));
      if (complianceDraft && prev) {
        const input = complianceInputOf(complianceDraftOf(prev, matrixRowOf(id_1)), complianceDraft);
        if (input) {
          await api.compliance.setCategory(id_1, input);
          void queryClient.invalidateQueries({
            queryKey: complianceMatrixKey
          });
        }
      }
    });
    if (ok_0) {
      setEditing(null);
      setComplianceDraft(null);
    }
  };

  // Сдвиг среди соседей того же уровня. Порядок пересчитываем у всей группы
  // (10, 20, 30…): у сида бывают одинаковые sortOrder, и обмен двух значений
  // тогда ничего не менял бы.
  const move = (c_0: Category, dir: -1 | 1) => {
    const siblings = activeSiblings(c_0.parentId);
    const idx = siblings.findIndex(s_0 => s_0.id === c_0.id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= siblings.length) return;
    const next_0 = [...siblings];
    [next_0[idx], next_0[j]] = [next_0[j] as Category, next_0[idx] as Category];
    void run(async () => {
      for (const [i, s_1] of next_0.entries()) {
        const sortOrder = (i + 1) * 10;
        if (s_1.sortOrder !== sortOrder) await api.categories.update(s_1.id, {
          sortOrder
        });
      }
    });
  };
  const setArchived = async (c_1: Category, archived: boolean) => {
    const ok_1 = await run(() => api.categories.update(c_1.id, {
      archived
    }));
    if (ok_1) setArchiving(null);
  };
  const facetLabel = (row_1: CategoryTreeRow) => {
    const section = row_1.section.value;
    const gender = row_1.gender.value;
    const inherited = !!row_1.section.inheritedFrom || !!row_1.gender.inheritedFrom;
    if (!section && !gender) return <span className="text-muted-foreground">{t("categories.facetsNone")}</span>;
    return <span className="flex flex-wrap items-center gap-1">
				{section && <Badge variant="outline">{t(`facets.sections.${section}`)}</Badge>}
				{gender && <Badge variant="outline">{t(`facets.genders.${gender}`)}</Badge>}
				{inherited && <span className="text-xs text-muted-foreground">{t("categories.inherited")}</span>}
			</span>;
  };
  const parentSelect = (value: string, onChange: (v: string) => void, selfId?: string) => {
    const options = allowedParents(tree, selfId);
    const current = value && !options.some(o => o.category.id === value) ? tree.byId.get(value) : undefined;
    return <Select value={value} title={t("categories.tree.depthHint")} data-testid="category-parent-select" onChange={e_0 => onChange(e_0.target.value)}>
				<option value="">{t("categories.noParent")}</option>
				{current && <option value={current.category.id}>{current.path.join(" / ")}</option>}
				{options.map(p_0 => <option key={p_0.category.id} value={p_0.category.id}>
						{`${"— ".repeat(p_0.depth - 1)}${p_0.category.name}`}
					</option>)}
			</Select>;
  };
  const gridLabel = (row_2: CategoryTreeRow) => {
    const grid = row_2.sizeGrid.value;
    if (!grid) return <span className="text-muted-foreground" data-testid={`category-grid-${row_2.category.id}`} data-grid="">
					{t("categories.tree.gridNone")}
				</span>;
    const from = row_2.sizeGrid.inheritedFrom ? tree.byId.get(row_2.sizeGrid.inheritedFrom) : undefined;
    return <span className="flex flex-col" data-testid={`category-grid-${row_2.category.id}`} data-grid={grid} data-inherited-from={from?.category.id ?? ""}>
				<span>{SIZE_GRIDS[grid as keyof typeof SIZE_GRIDS]?.label ?? grid}</span>
				{from && <span className="text-xs text-muted-foreground">
						{t("categories.tree.inheritedFrom", {
          name: from.category.name
        })}
					</span>}
			</span>;
  };
  const gridSelect = (draft_1: Draft, set: (d: Draft) => void) => <Select value={draft_1.sizeGrid} onChange={e_1 => set({
    ...draft_1,
    sizeGrid: e_1.target.value
  })} data-testid="category-grid-select">
			<option value="">{t("categories.tree.gridInherit")}</option>
			{SIZE_GRID_IDS.map(id_2 => <option key={id_2} value={id_2}>
					{SIZE_GRIDS[id_2].label}
				</option>)}
		</Select>;
  const facetSelects = (draft_2: Draft, set_0: (d: Draft) => void) => <>
			<Select value={draft_2.section} onChange={e_2 => set_0({
      ...draft_2,
      section: e_2.target.value
    })} aria-label={t("facets.section")}>
				<option value="">{t("facets.none")}</option>
				{SECTIONS.map(s_2 => <option key={s_2} value={s_2}>
						{t(`facets.sections.${s_2}`)}
					</option>)}
			</Select>
			<Select value={draft_2.gender} onChange={e_3 => set_0({
      ...draft_2,
      gender: e_3.target.value
    })} aria-label={t("facets.gender")}>
				<option value="">{t("facets.none")}</option>
				{GENDERS.map(g => <option key={g} value={g}>
						{t(`facets.genders.${g}`)}
					</option>)}
			</Select>
		</>;
  const productsCell = (row_3: CategoryTreeRow) => {
    const c_2 = row_3.category;
    const own = c_2.productCount ?? 0;
    return <div className="flex flex-col" data-testid={`category-count-${c_2.id}`} data-branch={row_3.branchProducts}>
				{row_3.branchProducts > 0 ? <Link to={`/products?categoryId=${encodeURIComponent(c_2.id)}`} className="text-primary hover:underline">
						{t("categories.productCount", {
          count: row_3.branchProducts,
          published: row_3.branchPublished
        })}
					</Link> : <span className="text-muted-foreground">{t("categories.noProducts")}</span>}
				{!row_3.isLeaf && own > 0 &&
      // Товар в промежуточном узле — след старого дерева: продавец
      // сейчас кладёт только в конечный узел.
      <span className="text-xs text-amber-700 dark:text-amber-400">
						{t("categories.tree.ownProducts", {
          count: own
        })}
					</span>}
			</div>;
  };
  const renderRow = (row_4: CategoryTreeRow, mode: "active" | "archive") => {
    const c_3 = row_4.category;
    if (mode === "active" && isHidden(row_4)) return null;
    const siblings_0 = mode === "active" ? activeSiblings(c_3.parentId) : [];
    const idx_0 = siblings_0.findIndex(s_3 => s_3.id === c_3.id);
    const indent = {
      paddingLeft: `${mode === "active" ? (row_4.depth - 1) * 24 + 8 : 8}px`
    };
    if (editing?.id === c_3.id) {
      const set_1 = (draft_3: Draft) => setEditing({
        ...editing,
        draft: draft_3
      });
      return <TableRow key={c_3.id} data-testid={`category-row-${c_3.id}`} data-editing="true">
					<TableCell style={indent}>
						<div className="flex flex-col gap-2">
							<Input autoFocus value={editing.draft.name} aria-label={t("categories.col.name")} data-testid="category-name-input" onChange={e_4 => set_1({
              ...editing.draft,
              name: e_4.target.value
            })} onKeyDown={e_5 => {
              if (e_5.key === "Enter") void submitEdit();
              if (e_5.key === "Escape") setEditing(null);
            }} />
							<label className="flex flex-col gap-1 text-xs text-muted-foreground">
								{t("categories.col.parent")}
								{parentSelect(editing.draft.parentId, v => set_1({
                ...editing.draft,
                parentId: v
              }), c_3.id)}
							</label>
							<label className="flex flex-col gap-1 text-xs text-muted-foreground">
								{t("categories.tree.image")}
								<ImageField value={editing.draft.imageUrl} onChange={v_0 => set_1({
                ...editing.draft,
                imageUrl: v_0
              })} />
							</label>
						</div>
					</TableCell>
					<TableCell>{gridSelect(editing.draft, set_1)}</TableCell>
					<TableCell>
						<div className="flex flex-col gap-2">{facetSelects(editing.draft, set_1)}</div>
						<p className="mt-1 max-w-[260px] text-xs text-muted-foreground">{t("facets.categoryHint")}</p>
					</TableCell>
					<TableCell className="min-w-[260px] align-top">
						{complianceDraft && <ComplianceEditor value={complianceDraft} onChange={setComplianceDraft} parentRequired={c_3.parentId ? compliance.get(c_3.parentId)?.required ?? false : false} categoryId={c_3.id} />}
					</TableCell>
					<TableCell />
					<TableCell className="text-right">
						<div className="flex justify-end gap-2">
							<Button size="sm" onClick={submitEdit} disabled={busy || !editing.draft.name.trim()} data-testid="category-save">
								{t("categories.save")}
							</Button>
							<Button size="sm" variant="outline" onClick={() => {
              setEditing(null);
              setComplianceDraft(null);
            }} disabled={busy} aria-label={t("categories.cancel")}>
								<X className="size-4" />
							</Button>
						</div>
					</TableCell>
				</TableRow>;
    }
    const isCollapsed = collapsed.has(c_3.id);
    // В архивном блоке кнопка «Вернуть» — только у самого архивного узла;
    // его потомки вернутся вместе с ним.
    const archivedAncestor = mode === "archive" && !c_3.archived ? (() => {
      for (let p_1 = c_3.parentId; p_1; p_1 = byId.get(p_1)?.parentId ?? null) if (byId.get(p_1)?.archived) return byId.get(p_1);
      return undefined;
    })() : undefined;
    return [<TableRow key={c_3.id} data-testid={`category-row-${c_3.id}`} data-depth={row_4.depth} data-archived={row_4.archived ? "true" : "false"} className={row_4.archived ? "text-muted-foreground" : undefined}>
				<TableCell style={indent}>
					<div className="flex items-center gap-2">
						{mode === "active" && row_4.hasChildren ? <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => toggle(c_3.id)} aria-label={isCollapsed ? t("categories.tree.expand") : t("categories.tree.collapse")} aria-expanded={!isCollapsed} data-testid={`category-toggle-${c_3.id}`}>
								{isCollapsed ? <ChevronRight className="size-4" /> : <ChevronDown className="size-4" />}
							</button> : <span className="inline-block w-4" />}
						<CategoryImage url={c_3.imageUrl} label={t("categories.tree.imageNone")} />
						<div className="flex min-w-0 flex-col">
							<span className={row_4.depth === 1 ? "font-semibold" : row_4.depth === 2 ? "font-medium" : undefined}>
								{mode === "archive" ? row_4.path.join(" / ") : c_3.name}
							</span>
							{mode === "active" && isCollapsed && <span className="text-xs text-muted-foreground">
									{t("categories.tree.childrenHidden", {
                count: tree.active.filter(r_3 => r_3.category.parentId === c_3.id).length
              })}
								</span>}
							{archivedAncestor && <span className="text-xs">{t("categories.tree.archivedWith", {
                name: archivedAncestor.name
              })}</span>}
						</div>
						{mode === "active" && row_4.isLeaf && row_4.depth > 1 && <Badge variant="outline" className="font-normal text-muted-foreground" title={t("categories.tree.leafHint")}>
								{t("categories.tree.leaf")}
							</Badge>}
						{c_3.archived && <Badge variant="outline">{t("categories.tree.archivedBadge")}</Badge>}
					</div>
				</TableCell>
				<TableCell className="text-sm">{gridLabel(row_4)}</TableCell>
				<TableCell>{facetLabel(row_4)}</TableCell>
				<TableCell>
					<ComplianceCell category={c_3} resolved={compliance.get(c_3.id)} nameOf={id_3 => byId.get(id_3)?.name ?? id_3} />
				</TableCell>
				<TableCell className="whitespace-nowrap tabular-nums">{productsCell(row_4)}</TableCell>
				<TableCell className="text-right">
					<div className="flex justify-end gap-1">
						{mode === "active" && canEdit && <>
								<Button size="sm" variant="ghost" onClick={() => move(c_3, -1)} disabled={busy || idx_0 <= 0} aria-label={t("categories.moveUp")} data-testid={`category-up-${c_3.id}`}>
									<ArrowUp className="size-4" />
								</Button>
								<Button size="sm" variant="ghost" onClick={() => move(c_3, 1)} disabled={busy || idx_0 < 0 || idx_0 >= siblings_0.length - 1} aria-label={t("categories.moveDown")} data-testid={`category-down-${c_3.id}`}>
									<ArrowDown className="size-4" />
								</Button>
							</>}
						<Button size="sm" variant={schemaOpen === c_3.id ? "default" : "ghost"} onClick={() => setSchemaOpen(schemaOpen === c_3.id ? null : c_3.id)} aria-label={t("categories.tree.schema")} title={t("categories.tree.schema")} data-testid={`category-schema-toggle-${c_3.id}`}>
							<ListChecks className="size-4" />
						</Button>
						{mode === "active" && canEdit && <Button size="sm" variant="outline" onClick={() => {
            setComplianceDraft(complianceDraftOf(c_3, matrixRowOf(c_3.id)));
            setEditing({
              id: c_3.id,
              draft: {
                name: c_3.name,
                parentId: c_3.parentId ?? "",
                section: c_3.section ?? "",
                gender: c_3.gender ?? "",
                sizeGrid: c_3.sizeGrid ?? "",
                imageUrl: c_3.imageUrl ?? ""
              }
            });
          }} disabled={busy} aria-label={t("categories.edit")} title={t("categories.edit")} data-testid={`category-edit-${c_3.id}`}>
								<Pencil className="size-4" />
							</Button>}
						{canEdit && c_3.archived && <Button size="sm" variant="outline" onClick={() => void setArchived(c_3, false)} disabled={busy} title={t("categories.tree.restore")} data-testid={`category-restore-${c_3.id}`}>
								<ArchiveRestore className="size-4" /> {t("categories.tree.restore")}
							</Button>}
						{canEdit && mode === "active" && <Button size="sm" variant="outline" onClick={() => setArchiving(row_4)} disabled={busy} aria-label={t("categories.tree.archive")} title={t("categories.tree.archive")} data-testid={`category-archive-${c_3.id}`}>
								<Archive className="size-4" />
							</Button>}
					</div>
				</TableCell>
			</TableRow>, schemaOpen === c_3.id ? <TableRow key={`${c_3.id}-schema`} className="bg-muted/30 hover:bg-muted/30">
					<TableCell colSpan={COLS} style={indent}>
						<div className="space-y-2 py-1">
							<p className="text-sm font-medium">
								{t("categories.tree.schemaTitle")}: {row_4.path.join(" / ")}
							</p>
							<CategorySchemaEditor row={row_4} tree={tree} canEdit={canEdit} />
						</div>
					</TableCell>
				</TableRow> : null];
  };
  const archivingChildren = archiving ? subtreeIds(all, archiving.category.id).size - 1 : 0;
  return <div className="space-y-4">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="space-y-1">
					<h1 className="text-xl font-semibold">{t("categories.heading")}</h1>
					<p className="max-w-3xl text-sm text-muted-foreground">{t("categories.tree.introV2")}</p>
					<p className="max-w-3xl text-sm text-muted-foreground">{t("compliance.category.intro")}</p>
					{complianceNotice && <p className="text-sm text-emerald-700" role="status" data-testid="category-compliance-notice">
							{complianceNotice}
						</p>}
				</div>
				<div className="flex flex-wrap gap-2">
					<Button variant="outline" onClick={() => setCollapsed(new Set())} data-testid="category-expand-all">
						{t("categories.tree.expandAll")}
					</Button>
					<Button variant="outline" onClick={() => setCollapsed(new Set(tree.active.filter(r_4 => r_4.hasChildren).map(r_5 => r_5.category.id)))} data-testid="category-collapse-all">
						{t("categories.tree.collapseAll")}
					</Button>
					{canEdit && <ApplyRecommendedButton all={all} onDone={count => {
          setComplianceNotice(t("compliance.category.applyRecommendedDone", {
            count
          }));
          void refresh();
          void queryClient.invalidateQueries({
            queryKey: complianceMatrixKey
          });
        }} />}
					{canEdit && <Button onClick={() => setCreating({
          ...blankDraft
        })} disabled={!!creating} data-testid="category-add">
							<Plus className="size-4" /> {t("categories.add")}
						</Button>}
				</div>
			</div>

			{items && <div className="flex flex-wrap gap-2 text-sm text-muted-foreground" data-testid="category-summary">
					<span>{t("categories.tree.summaryRoots", {
          count: tree.active.filter(r_6 => r_6.depth === 1).length
        })}</span>
					<span>·</span>
					<span>{t("categories.tree.summaryNodes", {
          count: tree.active.length
        })}</span>
					<span>·</span>
					<span>{t("categories.tree.summaryLeaves", {
          count: tree.active.filter(r_7 => r_7.isLeaf).length
        })}</span>
					{tree.archived.length > 0 && <>
							<span>·</span>
							<span>{t("categories.tree.summaryArchived", {
            count: tree.archived.length
          })}</span>
						</>}
				</div>}

			{sectionCounts.data && sectionCounts.data.length > 0 && <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 p-3 text-sm" data-testid="section-counts">
					<span className="text-muted-foreground">{t("categories.sectionCounts")}</span>
					{sectionCounts.data.map(row_5 => <Badge key={row_5.section} variant={row_5.section === "не задан" ? "destructive" : "outline"}>
							{row_5.section === "не задан" ? t("categories.sectionUnset") : t(`facets.sections.${row_5.section}`, {
          defaultValue: row_5.section
        })}
							: {row_5.count}
						</Badge>)}
				</div>}

			{error && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive" data-testid="category-error">
					{error}
				</div>}

			{creating && <div data-testid="category-create" className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-4">
					<label className="flex flex-col gap-1">
						<span className="text-sm text-muted-foreground">{t("categories.col.name")}</span>
						<Input autoFocus value={creating.name} onChange={e_6 => setCreating({
          ...creating,
          name: e_6.target.value
        })} onKeyDown={e_7 => {
          if (e_7.key === "Enter") void submitCreate();
        }} className="w-[260px]" data-testid="category-create-name" />
					</label>
					<label className="flex flex-col gap-1">
						<span className="text-sm text-muted-foreground">{t("categories.col.parent")}</span>
						{parentSelect(creating.parentId, v_1 => setCreating({
          ...creating,
          parentId: v_1
        }))}
					</label>
					<label className="flex flex-col gap-1">
						<span className="text-sm text-muted-foreground">
							{t("facets.section")} / {t("facets.gender")}
						</span>
						<div className="flex gap-2">{facetSelects(creating, setCreating)}</div>
					</label>
					<label className="flex flex-col gap-1">
						<span className="text-sm text-muted-foreground">{t("categories.tree.colGrid")}</span>
						{gridSelect(creating, setCreating)}
					</label>
					<label className="flex flex-col gap-1">
						<span className="text-sm text-muted-foreground">{t("categories.tree.image")}</span>
						<ImageField value={creating.imageUrl} onChange={v_2 => setCreating({
          ...creating,
          imageUrl: v_2
        })} />
					</label>
					<Button onClick={submitCreate} disabled={busy || !creating.name.trim()} data-testid="category-create-save">
						{t("categories.save")}
					</Button>
					<Button variant="outline" onClick={() => setCreating(null)} disabled={busy}>
						<X className="size-4" /> {t("categories.cancel")}
					</Button>
				</div>}

			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>{t("categories.col.name")}</TableHead>
						<TableHead>{t("categories.tree.colGrid")}</TableHead>
						<TableHead>{t("categories.col.facets")}</TableHead>
						<TableHead title={t("compliance.category.intro")}>{t("compliance.category.col")}</TableHead>
						<TableHead title={t("categories.tree.countHint")}>{t("categories.tree.colBranchProducts")}</TableHead>
						<TableHead className="w-[240px] text-right">{t("products.col.actions")}</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{items === null && !loadError && <TableRow>
							<TableCell colSpan={COLS} className="text-center text-muted-foreground">
								{t("categories.loading")}
							</TableCell>
						</TableRow>}
					{items !== null && items.length === 0 && <TableRow>
							<TableCell colSpan={COLS} className="text-center text-muted-foreground">
								{t("categories.empty")}
							</TableCell>
						</TableRow>}
					{tree.active.map(row_6 => renderRow(row_6, "active"))}
					{tree.orphans.map(row_7 => renderRow(row_7, "active"))}
				</TableBody>
			</Table>

			{tree.archived.length > 0 && <section className="space-y-2" data-testid="category-archive-block">
					<h2 className="text-base font-semibold">{t("categories.tree.archivedTitle")}</h2>
					<p className="max-w-3xl text-sm text-muted-foreground">{t("categories.tree.archivedHint")}</p>
					<Table>
						<TableBody>{tree.archived.map(row_8 => renderRow(row_8, "archive"))}</TableBody>
					</Table>
				</section>}

			<ConfirmDialog open={!!archiving} busy={busy} title={archiving ? t("categories.tree.archiveConfirmTitle", {
      name: archiving.category.name
    }) : ""} confirmLabel={t("categories.tree.archive")} onConfirm={() => archiving && void setArchived(archiving.category, true)} onClose={() => setArchiving(null)}>
				{archiving && <div className="space-y-2 text-sm" data-testid="category-archive-dialog">
						<p>{t("categories.tree.archiveConfirmBody", {
            count: archiving.branchProducts
          })}</p>
						{archivingChildren > 0 && <p className="text-muted-foreground">{t("categories.tree.archiveChildren", {
            count: archivingChildren
          })}</p>}
						{archiving.branchProducts > 0 && <p>
								<Link to={`/products?categoryId=${encodeURIComponent(archiving.category.id)}`} className="text-primary hover:underline">
									{t("categories.tree.archiveOpenProducts")}
								</Link>
							</p>}
					</div>}
			</ConfirmDialog>
		</div>;
}